/**
 * KeyManager manages user encryption keys in memory.
 *
 * Provides methods for setup, unlock, encryption/decryption, and locking.
 * Private keys are kept in memory only when unlocked and cleared on lock.
 *
 * @module key-manager
 */

import {
  type EncryptedData,
  type WrappedPrivateKey,
  bufferToBase64Url,
  decryptData,
  deriveKEK,
  encryptData,
  generateDek,
  generateKeyPair,
  unwrapPrivateKey,
  wrapPrivateKey,
} from "./crypto";

/** Result of a KeyManager setup operation. */
export interface SetupResult {
  /** The user's public key (exported as base64url) */
  publicKey: string;
  /** The wrapped private key for storage */
  wrappedPrivateKey: WrappedPrivateKey;
  /** The salt used for KEK derivation */
  salt: string;
  /** The raw private key (for recovery wrapping before discarding) */
  privateKey: CryptoKey;
}

/**
 * KeyManager manages cryptographic key operations in memory.
 *
 * The private key is only kept in memory while unlocked and is cleared
 * when lock() is called or when the manager instance is garbage collected.
 */
export class KeyManager {
  private privateKey: CryptoKey | null = null;
  private salt: string | null = null;
  private wrappedPrivateKey: WrappedPrivateKey | null = null;
  private deks: Map<string, CryptoKey> = new Map();

  // eslint-disable-next-line no-empty-function -- singleton pattern
  private constructor() {}

  /**
   * Sets up a new user's encryption keys.
   *
   * Generates a new key pair, wraps the private key with a PIN-derived KEK,
   * and returns the data needed for server storage.
   */
  static async setup(pin: string): Promise<SetupResult> {
    const keyPair = await generateKeyPair();
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const saltHex = Array.from(salt)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    const kek = await deriveKEK(pin, saltHex);
    const wrapped = await wrapPrivateKey(keyPair.privateKey, kek);

    // Export public key as base64url
    const publicKeyBytes = await crypto.subtle.exportKey(
      "raw",
      keyPair.publicKey,
    );
    const publicKeyBase64 = bufferToBase64Url(publicKeyBytes);

    return {
      publicKey: publicKeyBase64,
      wrappedPrivateKey: wrapped,
      salt: saltHex,
      privateKey: keyPair.privateKey,
    };
  }

  /**
   * Unlocks a KeyManager with the user's PIN and stored wrapped key.
   *
   * @throws {Error} If the PIN is incorrect (decryption fails)
   */
  static async unlock(
    wrappedPrivateKey: WrappedPrivateKey,
    salt: string,
    pin: string,
  ): Promise<KeyManager> {
    const kek = await deriveKEK(pin, salt);
    const privateKey = await unwrapPrivateKey(wrappedPrivateKey, kek);

    const manager = new KeyManager();
    manager.wrappedPrivateKey = wrappedPrivateKey;
    manager.salt = salt;
    manager.privateKey = privateKey;
    return manager;
  }

  /** Locks the KeyManager, clearing sensitive data from memory. */
  lock(): void {
    this.privateKey = null;
    this.deks.clear();
  }

  isUnlocked(): boolean {
    return this.privateKey !== null;
  }

  /**
   * Encrypts data using a newly generated DEK.
   * The DEK is kept in memory for later retrieval. Wrap it via wrapDek() for persistence.
   */
  async encryptData(data: string, dekId?: string): Promise<EncryptedData> {
    if (!this.privateKey)
      throw new Error("KeyManager is locked. Call unlock() first.");
    const dek = await generateDek();
    if (dekId) this.deks.set(dekId, dek);
    return encryptData(data, dek);
  }

  /** Decrypts data using a DEK stored in memory. */
  async decryptData(encrypted: EncryptedData, dekId: string): Promise<string> {
    if (!this.privateKey)
      throw new Error("KeyManager is locked. Call unlock() first.");
    const dek = this.deks.get(dekId);
    if (!dek) throw new Error(`DEK with id ${dekId} not found in memory.`);
    const decryptedBytes = await decryptData(encrypted, dek);
    return new TextDecoder().decode(decryptedBytes);
  }

  storeDek(dekId: string, dek: CryptoKey): void {
    this.deks.set(dekId, dek);
  }

  removeDek(dekId: string): void {
    this.deks.delete(dekId);
  }

  getPrivateKey(): CryptoKey {
    if (!this.privateKey)
      throw new Error("KeyManager is locked. Call unlock() first.");
    return this.privateKey;
  }

  getWrappedPrivateKey(): WrappedPrivateKey | null {
    return this.wrappedPrivateKey;
  }

  getSalt(): string | null {
    return this.salt;
  }

  /** Verifies that a PIN is correct without changing state. */
  async verifyPin(pin: string): Promise<boolean> {
    if (!this.wrappedPrivateKey || !this.salt) return false;
    try {
      const kek = await deriveKEK(pin, this.salt);
      await unwrapPrivateKey(this.wrappedPrivateKey, kek);
      return true;
    } catch {
      return false;
    }
  }
}
